import AppActivityIndicator from "@toothfairy/shared-ui/AppActivityIndicator";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import { showSideMenu } from "../../../../../App";
import AgentFunctions from "../../../../API/AgentFunctions";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import hostingsHelper from "../../../../API/Hostings/hostingsHelper";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";

const HostingsList = ({ isFunctionsLoading, setDisplayedView }) => {
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { getActiveWorkspace } = useWorkspaces();
  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedHostingId, setSelectedHostingId] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [displayedHostings, setDisplayedHostings] = React.useState([]);
  const [selectedHostingIds, setSelectedHostingIds] = React.useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = React.useState(false);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { height } = useWindowDimensions();
  const history = useHistory();
  const params = useParams();
  useEffect(() => {
    const optId = params?.optId;
    if (!optId) return;
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    if (isLoading) return;
    history.replace(`/workspaces/${wsId}/hostings/${optId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.optId, isLoading, getActiveWorkspace()?.id]);
  const columns = [
    {
      title: t("settings.list.id"),
      width: 0.00001,
    },
    {
      title: t("settings.list.name"),
      width: 0.25,
    },
    {
      title: t("settings.list.description"),
      // width: 0.25,
    },
    {
      title: t("settings.list.type"),
      width: 0.25,
    },
    {
      title: t("settings.list.actions"),
      isAction: true,
      width: 0.1,
    },
  ];
  const loadHostings = async (workspaceID) => {
    try {
      setIsLoading(true);
      const hostingsData = await hostingsHelper.getHostings(workspaceID, null);
      let hostings = hostingsData?.data?.HostingsByWorkspace?.items;
      hostings =
        hostings
          ?.map((u) => {
            if (u?._deleted !== true)
              return {
                ...u,
              };
          })
          ?.filter((u) => u != null) || [];
      dispatch("SET_HOSTINGS", hostings || []);

      setDisplayedHostings(hostings);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    loadHostings(state?.activeWorkspaceID);
  }, [state?.activeWorkspaceID]);
  useEffect(() => {
    setDisplayedHostings([...state?.hostings]);
  }, [JSON.stringify([...state?.hostings])]);

  function identifyOptByID(id) {
    return state.hostings.find((u) => u.id === id);
  }
  const handleHostingItemSelection = ({ id, item, index }) => {
    const wsId = getActiveWorkspace()?.id;
    const _id = id || item?.id;
    if (!wsId || !_id) return;
    history.push(`/workspaces/${wsId}/hostings/${_id}`);
  };
  const handleHostingItemDeletion = async (item, index) => {
    try {
      setShowChoice(false);
      const _id = selectedHostingId || selectedItem?.id;
      if (_id) {
        const functionsData =
          await AgentFunctions.getAgentHostingsByWorkspaceID({
            workspaceID: getActiveWorkspace()?.id,
          });
        let functions = functionsData?.data?.HostingsByWorkspace?.items;
        functions =
          functions
            ?.map((u) => {
              if (u?.llmProvider === _id)
                return {
                  ...u,
                };
            })
            ?.filter((u) => u != null && u?._deleted != true) || [];
        if (functions.length > 0) {
          AppToasty.show(t("hostingDeletionErrorCaptionDueToConflict"), {
            type: "danger",
          });
          return;
        }
        const result = await hostingsHelper.deleteHosting(_id);
        dispatch("SET_HOSTINGS", [
          ...state?.hostings.filter((u) => u.id !== _id),
        ]);
        AppToasty.show(t("hostingDeletionSuccessCaption"), {
          type: "success",
        });
        setSelectedIndex(null);
        setSelectedHostingId(null);
        setSelectedItem(null);
      } else {
        throw new Error("No item selected");
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("authorisationDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleBulkDeletion = async () => {
    try {
      let skipped = 0;
      for (const id of selectedHostingIds) {
        const functionsData =
          await AgentFunctions.getAgentHostingsByWorkspaceID({
            workspaceID: getActiveWorkspace()?.id,
          });
        const inUse = (functionsData?.data?.HostingsByWorkspace?.items || [])
          .filter((u) => u != null && u?._deleted != true)
          .some((u) => u?.llmProvider === id);
        if (inUse) {
          skipped += 1;
          continue;
        }
        await hostingsHelper.deleteHosting(id);
      }
      dispatch("SET_HOSTINGS", [
        ...state?.hostings.filter((u) => !selectedHostingIds.includes(u.id)),
      ]);
      setSelectedHostingIds([]);
      setShowBulkDeleteChoice(false);
      if (skipped > 0) {
        AppToasty.show(t("hostingDeletionErrorCaptionDueToConflict"), {
          type: "danger",
        });
      } else {
        AppToasty.show(t("hostingDeletionSuccessCaption"), {
          type: "success",
        });
      }
    } catch (error) {
      console.log(error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("authorisationDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleCreation = () => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    history.push(`/workspaces/${wsId}/hostings/new`);
  };
  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancel")}
        caption={t("settings.list.deleteHostingConfirmation")}
        subCaption={t("settings.list.deleteHostingConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleHostingItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem]);
  const _bulkDeleteChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteSelectedConfirmation", {
          defaultValue: t("settings.list.deleteHostingConfirmation"),
        })}
        subCaption={t("settings.list.deleteHostingConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    );
  }, [showBulkDeleteChoice, selectedHostingIds]);

  return (
    <View
      style={{
        paddingLeft: 10,
        flex: 1,
        width: showSideMenu() ? width - 220 : "100%",
        minWidth: "100%",
        overflow: isLoading && "hidden",
      }}
    >
      <>
        {isLoading ? (
          <ToothFairyLoadingPlaceholder
            backgroundColor={currentTheme?.theme?.softBackgroundColor}
            visible={true}
            containerStyle={{
              backgroundColor: currentTheme?.theme?.transparent,
              minHeight: 250,
            }}
            caption={t("settings.list.loadingHostings")}
          />
        ) : (
          <>
            {displayedHostings.length > 0 ? (
              <AppTable
                isSelectable
                showPagination
                itemsPerPage={50}
                hideSearch={false}
                searchPlaceholder={t("settings.toolbar.searchHostings")}
                onSelectionChange={(selectedItems) => {
                  setSelectedHostingIds(selectedItems?.map((u) => u?.id) || []);
                }}
                bulkActions={[
                  {
                    id: "DELETE",
                    label: t("settings.list.delete"),
                    icon: AppAssets.icons.delete,
                    isDestructive: true,
                  },
                ]}
                onBulkActionPress={(id) => {
                  if (id === "DELETE" && selectedHostingIds?.length > 0)
                    setShowBulkDeleteChoice(true);
                }}
                actionButtons
                showDropDownMenuForActions
                mainActionLabel={t("settings.list.edit")}
                onClickMainAction={(item, index) => {
                  handleHostingItemSelection({
                    id: item?.id,
                    item,
                    index,
                  });
                }}
                onActionItemPress={(id, item, index) => {
                  if (id === "EDIT") {
                    handleHostingItemSelection({
                      id: item?.id,
                      item,
                      index,
                    });
                  } else if (id === "DELETE") {
                    setSelectedItem(item);
                    setSelectedIndex(index);
                    setSelectedHostingId(item?.id);
                    setShowChoice(true);
                  }
                }}
                actions={[
                  {
                    label: t("settings.list.edit"),
                    id: "EDIT",
                    icon: AppAssets.icons.edit,
                  },
                  {
                    label: t("settings.list.delete"),
                    id: "DELETE",
                    icon: AppAssets.icons.delete,
                  },
                ]}
                style={{
                  zIndex: -1,
                  marginTop: 15,
                  height:
                    displayedHostings?.length >= 50 ? height - 95 : height - 75,
                  paddingRight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
                headers={columns}
                items={displayedHostings
                  ?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
                  ?.map((u) => {
                    return {
                      id: u?.id || " ",
                      name: u?.name || " ",
                      description:
                        u?.description ||
                        t("settings.list.noDescriptionProvided"),
                      type: u?.type?.toUpperCase() || " ",
                      actions: " ",
                    };
                  })}
              >
                <ToothFairyButton
                  title={t("common.create")}
                  onPress={handleCreation}
                />
              </AppTable>
            ) : (
              <AppPlaceholderView
                wrapperStyle={{
                  width: "100%",
                  height: "100%",
                  marginVertical: 60,
                  marginHorizontal: "auto",
                  flex: 1,
                  zIndex: -1,
                }}
                size={30}
                icon={AppAssets.icons.hosting}
                mainCaption={t("settings.list.noHostingsFound")}
                subCaption={t("settings.list.noHostingsFoundSub")}
                actionLabel={t("common.create")}
                onActionPress={handleCreation}
              />
            )}
          </>
        )}
      </>

      {_coreChoice}
      {_bulkDeleteChoice}
    </View>
  );
};

export default HostingsList;
